import React, { useRef, useState } from 'react';
import { RegExpMatcher, englishRecommendedTransformers } from 'obscenity';
import dynamic from 'next/dynamic';
import { myDataset } from '../../data/obscenityDataset';
import { Highlighter } from './Highlighter';
import s from './ResponseDialog.module.css';
import {
    ResponseDialogProps,
    FormData,
    ResponseToSubmit,
} from '@/types/components/responseDialog';
const DynamicMiniMap = dynamic(() => import('./Minimap'), {
    ssr: false,
});

export default function ResponseDialog({
    locationcoordinates,
    host,
    portalId,
    pageId,
    respondForm,
    dialogReference,
    siteSetting,
    onSubmit,
    setSelectedGeolocation,
    responseDialogOpenedByButton,
    setResponseDialogOpenedByButton,
    resetGeolocation,
    portalPage,
    angle,
    is3d,
    searchBoxEnabled,
}: ResponseDialogProps) {
    const cleanFormData: FormData = {
        mood: respondForm.moods[0],
        name: '',
        email: '',
        locationName: '',
        text: '',
        visible: false,
        clientName: 'dummyName',
        theme: '',
        involvement: '',
        privacy: false,
        sendmail: false,
        location: null,
        location3d: null,
        pageId: null,
    };
    const [formData, setFormData] = useState(cleanFormData);
    const [profanity, setProfanity] = useState<
        { start: number; end: number }[]
    >([]);
    const formRef = useRef<HTMLFormElement | null>(null);

    function handleInputChange(
        e:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ): void {
        e.preventDefault();
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setProfanity([]);
    }

    function setLocation(location: any) {
        setSelectedGeolocation(location);

        setFormData({
            ...formData,
            location: {
                type: 'Point',
                coordinates: [location.x, location.y],
                srid: location.spatialReference.wkid,
            },
            location3d: location.z && {
                type: 'Point',
                coordinates: [location.x, location.y, location.z],
                srid: location.spatialReference.wkid,
            },
        });
    }

    function closeResponseDialog() {
        (formRef.current as HTMLFormElement | null)?.reset();
        //force reset on search widget
        (
            document.querySelectorAll(
                'dialog .esri-search__clear-button'
            )[0] as HTMLElement | null
        )?.click();

        if (dialogReference.current) {
            (dialogReference.current as { close: () => void }).close();
        }

        //reset states
        resetGeolocation(null);
        setFormData(cleanFormData);
        setProfanity([]);
        setResponseDialogOpenedByButton &&
            setResponseDialogOpenedByButton(false); //reset state
    }

    function moodToIcon(mood: string) {
        let option = '';

        if (mood === 'zorg') {
            option = 'comment';
        } else if (mood === 'suggestie') {
            option = 'lightbulb';
        } else if (mood === 'vraag') {
            option = 'contact_support';
        }

        return option;
    }

    function moodToText(mood: string) {
        const option = '';

        if (mood === 'zorg') {
            return respondForm.zorgLabel;
        } else if (mood === 'suggestie') {
            return respondForm.suggestieLabel;
        } else if (mood === 'vraag') {
            return respondForm.vraagLabel;
        }

        return option;
    }

    function isValidEmail(email: string) {
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailPattern.test(email);
    }

    //create a matcher to check for bad words
    const matcher = new RegExpMatcher({
        ...myDataset.build(),
        ...englishRecommendedTransformers,
    });

    function validateFormfield(
        input: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    ) {
        const errorId = input.getAttribute('aria-describedby');
        if (input.validity.valid) {
            input.setAttribute('aria-invalid', 'false');
            if (errorId) {
                document.getElementById(errorId)!.innerText = '';
            }
        } else {
            input.setAttribute('aria-invalid', 'true');
            if (errorId) {
                document.getElementById(errorId)!.innerText = document
                    .getElementById(errorId)!
                    .getAttribute('data-errortext')!;
            }
        }
    }

    const submitResponse = (e: React.FormEvent) => {
        e.preventDefault();

        const invalidInputs = formRef.current?.querySelectorAll(
            'input:invalid, textarea:invalid, select:invalid'
        );
        if (invalidInputs) {
            for (let i = 0; i < invalidInputs.length; i++) {
                validateFormfield(
                    invalidInputs[i] as
                        | HTMLInputElement
                        | HTMLTextAreaElement
                        | HTMLSelectElement
                );
            }
            if (invalidInputs.length > 0) {
                return;
            }
        }

        if (responseDialogOpenedByButton && searchBoxEnabled) {
            const searchWidget = document.getElementById(
                'ResponseDialog__searchWidget'
            );
            const searchInput = searchWidget?.querySelector(
                '.esri-search__input'
            ) as HTMLInputElement | null;
            formData.locationName = searchInput?.value || '';
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { theme, involvement, ...formDataCopy } = formData;

        const updatedFormData: ResponseToSubmit = {
            ...formDataCopy,
        };

        //check if the text contains any bad words
        const response = formData.text;
        const matches = matcher.getAllMatches(response, true);
        //if contains bad words set a warning and return
        if (matches.length > 0) {
            const hightlights = matches.map((match) => {
                const { startIndex, endIndex } =
                    myDataset.getPayloadWithPhraseMetadata(match);
                return {
                    start: startIndex,
                    end: endIndex,
                };
            });
            setProfanity(hightlights);
            return;
        }

        updatedFormData.theme = respondForm.themes.find(
            (theme) => theme.value === formData.theme
        );
        updatedFormData.involvement = respondForm.involvements.find(
            (involvement) => involvement.value === formData.involvement
        );

        updatedFormData.pageId = pageId;
        if (locationcoordinates) {
            //add coordinates-array to object
            updatedFormData.location = {
                type: 'Point',
                coordinates: [locationcoordinates.x, locationcoordinates.y],
                srid: locationcoordinates.spatialReference.wkid,
            };

            if (is3d) {
                updatedFormData.location3d = {
                    type: 'Point',
                    coordinates: [
                        locationcoordinates.x,
                        locationcoordinates.y,
                        locationcoordinates.z ? locationcoordinates.z : 0,
                    ],
                    srid: locationcoordinates.spatialReference.wkid,
                };
            }
            if (angle) {
                updatedFormData.angle = {
                    heading: angle?.heading ? angle.heading : 0,
                    tilt: angle?.tilt ? angle.tilt : 0,
                    zoom: angle?.zoom ? angle.zoom : 0,
                    scale: angle?.scale ? angle.scale : 0,
                };
            }
        }

        if (updatedFormData.sendmail) {
            if (isValidEmail(updatedFormData.email)) {
                updatedFormData.sendmail = true;
            } else {
                alert('Ongeldig e-mailadres');
                return;
            }
        }
        onSubmit(updatedFormData);
        closeResponseDialog();
    };

    return (
        <dialog
            data-cy="responsedialog"
            ref={dialogReference}
            aria-labelledby={respondForm.title}
            aria-modal="true"
            className={s['ResponseDialog']}>
            <div className={s['ResponseDialog__inner']}>
                <div className={s['ResponseDialog__header']}>
                    <h2 className={s['ResponseDialog__title']}>
                        {respondForm.title}
                    </h2>
                    <button type="button" onClick={() => closeResponseDialog()}>
                        <span className="material-icons">close</span>
                    </button>
                </div>

                <form
                    data-cy="responseDialogForm"
                    ref={formRef}
                    method="post"
                    onSubmit={submitResponse}
                    noValidate
                    className={s['ResponseDialog__form']}>
                    {respondForm.modalText && (
                        <p className={s['ResponseDialog__modaltext']}>
                            {respondForm.modalText}
                        </p>
                    )}
                    {searchBoxEnabled && (
                        <div
                            className={
                                s[
                                    responseDialogOpenedByButton == true
                                        ? 'ResponseDialog__searchWidget'
                                        : 'ResponseDialog__searchWidget--hidden'
                                ]
                            }
                            id="ResponseDialog__searchWidget"></div>
                    )}

                    <fieldset className={s['ResponseDialog__fieldset']}>
                        <legend className={s['responseDialogTheme']}>
                            {respondForm.typeQuestion}
                        </legend>
                        {respondForm.moods.map((mood, index) => (
                            <label
                                className={s['ResponseDialog__customradio']}
                                key={index}
                                htmlFor={`${mood}-${index}`}>
                                <span className="material-icons">
                                    {' '}
                                    {moodToIcon(mood)}
                                </span>
                                <input
                                    onChange={(e) => {
                                        const selectedMood = e.target.value;
                                        setFormData({
                                            ...formData,
                                            mood: selectedMood,
                                        });
                                    }}
                                    onInput={(e) =>
                                        validateFormfield(
                                            e.target as HTMLTextAreaElement
                                        )
                                    }
                                    type="radio"
                                    name="mood"
                                    value={mood}
                                    id={`${mood}-${index}`}
                                    checked={formData.mood === mood}
                                />{' '}
                                {moodToText(mood)}
                            </label>
                        ))}
                    </fieldset>
                    <div
                        className={
                            s[
                                respondForm.themes?.length > 0
                                    ? 'ResponseDialog__inputgroup'
                                    : 'ResponseDialog__inputgroup--hidden'
                            ]
                        }>
                        <label htmlFor="responseDialogTheme">
                            {respondForm.reactionQuestion}
                        </label>
                        <select
                            data-cy="responseDialogTheme"
                            className={s['ResponseDialog__inputfield']}
                            name="theme"
                            title="theme"
                            id="responseDialogTheme"
                            onChange={handleInputChange}
                            onInput={(e) =>
                                validateFormfield(
                                    e.target as HTMLTextAreaElement
                                )
                            }
                            value={formData.theme}>
                            <option value="" disabled={false} hidden={false}>
                                {respondForm.reactionQuestionAdvice}
                            </option>
                            {respondForm.themes?.map((th) => {
                                return (
                                    <option
                                        key={th.id + th.value}
                                        value={th.value}>
                                        {th.name}
                                    </option>
                                );
                            })}
                        </select>
                    </div>

                    <div className={s['ResponseDialog__inputgroup']}>
                        <label htmlFor="text">
                            {respondForm.sharingQuestion}
                            <span> *</span>
                        </label>
                        {profanity.length > 0 ? (
                            <Highlighter
                                text={formData.text}
                                highlights={profanity}
                                returnTextArea={setProfanity}
                            />
                        ) : (
                            <>
                                <textarea
                                    data-cy="responseDialogText"
                                    className={`${s['ResponseDialog__inputfield']}`}
                                    id="text"
                                    name="text"
                                    placeholder={
                                        respondForm.sharingQuestionAdvice
                                    }
                                    value={formData.text}
                                    onChange={handleInputChange}
                                    onInput={(e) =>
                                        validateFormfield(
                                            e.target as HTMLTextAreaElement
                                        )
                                    }
                                    required
                                    minLength={1}
                                    maxLength={1000}
                                    aria-invalid="false"
                                    aria-describedby="error_text_id"></textarea>
                                <div
                                    id="error_text_id"
                                    aria-live="polite"
                                    data-errortext={
                                        respondForm.sharingQuestionValidation
                                    }></div>
                            </>
                        )}
                        {profanity.length > 0 && (
                            <small className={s.ResponseDialog_warning}>
                                {respondForm.sharingQuestionProfanityWarning}
                            </small>
                        )}
                        <small>{respondForm.sharingQuestionNote}</small>
                    </div>
                    <div
                        className={
                            s[
                                respondForm.involvements?.length > 0
                                    ? 'ResponseDialog__inputgroup'
                                    : 'ResponseDialog__inputgroup--hidden'
                            ]
                        }>
                        <label htmlFor="responseDialogInvolvement">
                            {respondForm.involvementQuestion}
                        </label>
                        <select
                            data-cy="responseDialogInvolvement"
                            className={s['ResponseDialog__inputfield']}
                            name="involvement"
                            title="theme"
                            id="responseDialogInvolvement"
                            onChange={handleInputChange}
                            onInput={(e) =>
                                validateFormfield(
                                    e.target as HTMLTextAreaElement
                                )
                            }
                            value={formData.involvement}>
                            <option value="">
                                {respondForm.involvementQuestionAdvice}
                            </option>
                            {respondForm.involvements?.map(
                                (inv, key: number) => {
                                    return (
                                        <option
                                            key={inv.value + key}
                                            value={inv.value}>
                                            {inv.name}
                                        </option>
                                    );
                                }
                            )}
                        </select>
                    </div>
                    <div className={s['ResponseDialog__inputgroup']}>
                        <label htmlFor="responseDialogName">
                            {respondForm.nameQuestion}
                        </label>

                        <input
                            data-cy="responseDialogName"
                            className={s['ResponseDialog__inputfield']}
                            id="responseDialogName"
                            type="text"
                            name="name"
                            autoComplete="name"
                            placeholder={respondForm.nameQuestionAdvice}
                            value={formData.name}
                            onChange={handleInputChange}
                            onInput={(e) =>
                                validateFormfield(
                                    e.target as HTMLTextAreaElement
                                )
                            }
                        />
                        <small>{respondForm.nameQuestionNote}</small>
                    </div>

                    <fieldset className={s['ResponseDialog__fieldset']}>
                        <label>
                            <input
                                data-cy="responseDialogPrivacy"
                                id="privacy"
                                name="privacy"
                                type="checkbox"
                                required
                                defaultChecked={formData.privacy}
                                className={s['ResponseDialog__checkbox']}
                                aria-invalid="false"
                                aria-describedby="error_privacy_id"
                                onInput={(e) =>
                                    validateFormfield(
                                        e.target as HTMLTextAreaElement
                                    )
                                }
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    if (isChecked) {
                                        setFormData({
                                            ...formData,
                                            privacy: true,
                                        });
                                    }
                                }}></input>
                            <span>{respondForm.privacyQuestion}</span>

                            <a
                                href={respondForm.privacyStatement?.href}
                                rel="noopener noreferrer"
                                target="_blank">
                                {' '}
                                privacy statement
                            </a>
                            <span> *</span>
                        </label>
                    </fieldset>
                    <div
                        id="error_privacy_id"
                        aria-live="polite"
                        data-errortext={
                            respondForm.privacyQuestionValidation
                        }></div>
                    <fieldset className={s['ResponseDialog__fieldset']}>
                        <label>
                            <input
                                data-cy="responseDialogSendmail"
                                className={s['ResponseDialog__checkbox']}
                                id="sendmail"
                                name="sendmail"
                                type="checkbox"
                                checked={formData.sendmail}
                                onInput={(e) =>
                                    validateFormfield(
                                        e.target as HTMLTextAreaElement
                                    )
                                }
                                onChange={(e) => {
                                    const isChecked = e.target.checked;

                                    setFormData({
                                        ...formData,
                                        sendmail: isChecked,
                                    });
                                }}
                            />{' '}
                            {respondForm.contactQuestion}
                        </label>
                    </fieldset>
                    {formData.sendmail && (
                        <div className={s['ResponseDialog__inputgroup']}>
                            {respondForm.emailQuestion}
                            <input
                                data-cy="responseDialogEmail"
                                className={s['ResponseDialog__inputfield']}
                                id="responseDialogEmail"
                                type="email"
                                autoComplete="email"
                                placeholder={respondForm.emailQuestionAdvice}
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                onInput={(e) =>
                                    validateFormfield(
                                        e.target as HTMLTextAreaElement
                                    )
                                }
                                required
                                aria-invalid="false"
                                aria-describedby="error_email_id"
                            />
                            <div
                                id="error_email_id"
                                aria-live="polite"
                                data-errortext={
                                    respondForm.emailQuestionValidation
                                }></div>
                        </div>
                    )}

                    <div className={s['ResponseDialog__BottomRow']}>
                        <button
                            type="button"
                            onClick={() => closeResponseDialog()}>
                            <span>{respondForm.cancelQuestion}</span>
                        </button>
                        <button
                            className={s['ResponseDialog__submitbutton']}
                            type="submit">
                            {respondForm.submitQuestion}
                            <span className="material-icons">
                                arrow_forward
                            </span>
                        </button>
                    </div>
                </form>

                {portalPage && !is3d && (
                    <div className={s['ResponseDialog__minimap']}>
                        <div className={s['ResponseDialog__minimap__inner']}>
                            <DynamicMiniMap
                                zoom={12}
                                host={host}
                                center={locationcoordinates}
                                mapId={portalId}
                                selectedMood={formData.mood}
                                siteSetting={siteSetting}
                                setLocation={setLocation}
                            />
                        </div>
                    </div>
                )}
            </div>
        </dialog>
    );
}
